import {
  SALE_STATUS_NAME_MAP,
  SALE_STATUS_COLOR_MAP,
  SALE_PAY_METHOD_NAME_MAP,
  SALE_PAY_METHOD_COLOR_MAP
} from "@/constants/sales.js";
import { mapState } from "vuex";
import uniq from "lodash/uniq";
import moment from "moment";

const shape = "DD/MM/YYYY";

export default {
  data() {
    return {
      gradientColor: "#5576ec"
    };
  },
  computed: {
    ...mapState("product", ["products", "currencies", "paymentMethods"]),
    ...mapState("sale", ["sales", "filterDates"]),
    ...mapState("reports", ["reports"]),
    completeDayList() {
      const reversed = shape
        .split("")
        .reverse()
        .join("");
      const days = this.filterDates.length
        ? moment(this.filterDates[1], reversed).diff(
            moment(this.filterDates[0], reversed).subtract(1, "days"),
            "days"
          )
        : 7;
      const startFilter = this.filterDates[1]
        ? moment(this.filterDates[1], reversed)
        : moment();

      const dayList = Array(days)
        .fill("")
        .map((_, i) =>
          (i === 0 ? startFilter : startFilter.subtract(1, "days")).format(
            shape
          )
        );
      if (days == 1) {
        dayList.push(dayList[0]);
      }
      return dayList;
    },
    revenuesByDay() {
      var c = document.createElement("canvas");
      c.setAttribute("id", "gradient-chart");

      var ctx = c.getContext("2d");

      var gradient = ctx.createLinearGradient(0, 0, 0, 130);
      gradient.addColorStop(1, "rgba(255, 255, 255, 0.8)");
      // gradient.addColorStop(0, "rgba(145, 85, 236, 0.2)");
      gradient.addColorStop(0, `${this.gradientColor}20`);

      return {
        labels: this.reports.map(r =>
          r.label
            .split("-")
            .reverse()
            .join("/")
        ),
        datasets: [
          {
            data: this.reports.map(r => r.value),
            backgroundColor: gradient,
            borderColor: this.gradientColor,
            pointRadius: 3,
            pointBackgroundColor: this.gradientColor,
            tension: 0
          }
        ]
      };
    },
    revenuesByProduct() {
      const products = this.sales
        .map(s => ({
          id: s.product,
          name: s.product_name
        }))
        .sort((a, b) => {
          if (a.id < b.id) return 1;
          if (a.id > b.id) return 1;
          return 0;
        });
      const uniqProducts = uniq(products.map(p => p.id)).sort();

      const data = uniqProducts.map(product => {
        const productValue = this.sales.reduce(
          (acc, cur) =>
            cur.product === product ? Number(acc) + Number(cur.amount) : acc,
          0
        );
        return {
          product,
          value: productValue
        };
      });

      return {
        labels: products.map(p => p.name),
        datasets: [
          {
            data: data.map(p => p.value),
            backgroundColor: this.gradientColor,
            borderColor: this.gradientColor,
            pointRadius: 3,
            pointBackgroundColor: this.gradientColor,
            tension: 0,
            barPercentage: 0.6
          }
        ]
      };
    },
    salesByPaymentMethodData() {
      const uniqMethods = uniq(
        this.sales.map(({ payment_method }) => payment_method)
      );

      const data = uniqMethods.map(payment_method => {
        const list = this.sales.filter(
          s => s.payment_method === payment_method
        );
        return { payment_method, count: list.length };
      });
      const colors = data.map(d => SALE_PAY_METHOD_COLOR_MAP[d.payment_method]);
      return {
        legendData: data.map(d => ({
          color: SALE_PAY_METHOD_COLOR_MAP[d.payment_method],
          name: SALE_PAY_METHOD_NAME_MAP[d.payment_method],
          count: d.count,
          percent: ((d.count * 100) / this.sales.length).toFixed(2)
        })),
        chartData: {
          labels: data.map(d => SALE_PAY_METHOD_NAME_MAP[d.payment_method]),
          datasets: [
            {
              data: data.map(d => d.count),
              label: "",
              legend: true,
              backgroundColor: colors,
              borderColor: colors,
              hoverBackgroundColor: colors,
              hoverBorderColor: colors,
              barPercentage: 0.2
            }
          ]
        }
      };
    },
    salesByStatusData() {
      const uniqStatus = uniq(this.sales.map(({ status }) => status));

      const data = uniqStatus.map(status => {
        const list = this.sales.filter(s => s.status === status);
        return { status, count: list.length };
      });
      const colors = data.map(d => SALE_STATUS_COLOR_MAP[d.status]);
      return {
        legendData: data.map(d => ({
          color: SALE_STATUS_COLOR_MAP[d.status],
          name: SALE_STATUS_NAME_MAP[d.status],
          count: d.count,
          percent: ((d.count * 100) / this.sales.length).toFixed(2)
        })),
        chartData: {
          labels: data.map(d => SALE_STATUS_NAME_MAP[d.status]),
          datasets: [
            {
              data: data.map(d => d.count),
              label: "",
              legend: true,
              backgroundColor: colors,
              borderColor: colors,
              hoverBackgroundColor: colors,
              hoverBorderColor: colors,
              barPercentage: 0.2
            }
          ]
        }
      };
    },
    salesByUTMData() {
      return {
        utm_source: [
          { name: "email-mkt", value: 12997, percent: 82 },
          { name: "facebook", value: 32997, percent: 64 },
          { name: "instagram", value: 2997, percent: 48 },
          { name: "twitter", value: 192997, percent: 29.23 },
          { name: "landing-page", value: 42997, percent: 20 },
          { name: "linkedin", value: 22997, percent: 18 },
          { name: "tiktok", value: 82997, percent: 12.3 }
        ],
        utm_campaign: [
          { name: "email-mkt", value: 12997, percent: 67 },
          { name: "fb", value: 32997, percent: 64 },
          { name: "comunicado-oficial", value: 2997, percent: 48 },
          { name: "twitter", value: 192997, percent: 29.23 },
          { name: "aula-ao-vivo", value: 42997, percent: 20 },
          { name: "ofimdoquase", value: 22997, percent: 18 },
          { name: "carta-elton", value: 82997, percent: 12.3 }
        ]
      };
    },
    salesByDeviceData() {
      return {
        default: [
          { icon: "smartphone", name: "smartphone", value: 2997, percent: 60 },
          { icon: "desktop", name: "desktop", value: 22997, percent: 28 },
          { icon: "tablet", name: "tablet", value: 192997, percent: 12 }
        ]
      };
    },
    salesByAffiliateData() {
      return {
        7: this.revenuesByDay,
        30: this.revenuesByDay,
        90: this.revenuesByDay,
        180: this.revenuesByDay
      };
    },
    salesByDayHourData() {
      const hours = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22].map(
        (v, i) => `${v}h`
      );
      const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      //generating an object like this -> { series: [{ name: 'some_name', data: [{ x: "some legend", y: 20 }]}]}
      const data = {
        options: {
          chart: {
            type: "heatmap"
          },
          xaxis: {
            categories: hours
          },
          dataLabels: {
            enabled: false
          },
          theme: {
            monochrome: {
              enabled: true,
              color: "#ee6469",
              shadeTo: "light",
              shadeIntensity: 0.65
            }
          }
        },
        series: days.map(day => ({
          name: day,
          data: hours.map((hour, i) => ({ x: hour, y: i * Math.random() }))
        }))
      };
      return {
        7: data,
        30: data,
        90: data,
        180: data
      };
    }
  }
};
